import { z } from 'zod';
import { isAlphaNumericOrUnderscore } from './utils';

export enum TSA_VALUES {
  REQUIRED = 'required',
  ENABLED = 'enabled',
  DISABLED = '',
}
export enum TableMode {
  Create = 'create',
  Read = 'read',
  Edit = 'edit',
}

export enum TabValue {
  TableSchema,
  MappingConfig,
  ProductConfig,
}
export default function unique<T extends object>(
  uniqueKeys: { key: keyof T; message: string }[]
) {
  return (data: T[], ctx: z.RefinementCtx) => {
    uniqueKeys.forEach(({ key, message }) => {
      const seen = new Set();
      data.forEach((item, index) => {
        const value = item[key];
        if (seen.has(value)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message,
            path: [index, key.toString()], // .toString() in case `key` is typeof `symbol`.
          });
        } else {
          seen.add(value);
        }
      });
    });
  };
}
// TSA Schema
const tsaSchema = z.object({
  required: z.boolean(),
  enabled: z.boolean(),
});

// Mapping fields schema
const mappingFieldsSchema = z.object({
  mappingName: z.string(),
  targetName: z.string(),
  displayName: z.string(),
  descriptionName: z.string().nullable(),
  required: z.boolean().default(true).optional(), // Removed .nullable() as per the latest edits
  tsa: tsaSchema.nullable(),
});

// DSP schema
const dspSchema = z.object({
  required: z.boolean().default(true).nullable().optional(),
  submissionType: z.string().nullable().optional(),
  mappingFields: z.array(mappingFieldsSchema).nullable().optional(),
  defaultValue: z.string().nullable().optional(),
  tsa: tsaSchema.nullable().optional(),
});

// Table row schema
const tableRowSchema = z.object({
  name: z
    .string()
    .min(1, { message: 'Field name cannot be empty' })
    .max(60, {
      message: 'Field name must be less than 60 characters',
    })
    .refine((val) => isAlphaNumericOrUnderscore(val), {
      message:
        'Field name must not contain special characters except underscores (_)',
    }),
  displayName: z.string().nullable(),
  type: z.string().min(1, {
    message: "Field's Data Type is required",
  }),
  description: z.string().nullable(),
  dsp: dspSchema.nullable(),
});

// Target table form schema
export const targetTableFormSchema = z
  .object({
    tableName: z
      .string()
      .min(1, { message: 'Table name is required' })
      .max(60, { message: 'Table name must be less than 60 characters' }),
    description: z.string().nullable(),
    dsp: z.boolean().default(false),
    submissionType: z.string().nullable().optional(),
    orgSlug: z.string().nullable(),
    tableData: z
      .array(tableRowSchema)
      .min(1, { message: 'At least one field row is required' })
      .superRefine(
        unique([{ key: 'name', message: 'Target Field Name must be unique.' }])
      ),
  })
  .refine(
    (data) => {
      if (data.dsp && !data.submissionType) {
        return false;
      }
      return true;
    },
    {
      message: 'Submission type is required for DSP tables',
      path: ['submissionType'],
    }
  );

// Mapping fields table row schema
const mappingFieldsRowSchema = z.object({
  fieldName: z.string(),
  mappingName: z.string(),
  mappingValue: z.string().optional(),
  targetName: z.string(),
  descriptionName: z.string().nullable(),
  displayName: z.string(),
  required: z.boolean().default(true), // Removed .nullable() as per the latest edits
  displayPreference: z
    .enum(['do_not_display', 'submitted', 'mapped'])
    .optional(),
});

// Mapping fields table schema
export const mappingFieldsTableSchema = z.object({
  fields: z.array(mappingFieldsRowSchema),
});

// TSA fields row schema
const tsaFieldsRowSchema = z.object({
  fieldName: z.string().nullable(),
  tsa: z.nativeEnum(TSA_VALUES).nullable(),
});

// TSA fields table schema
export const tsaFieldsTableSchema = z.object({
  requiredFields: z.array(z.string()).optional(),
  fields: z.array(tsaFieldsRowSchema),
});

// Type exports
export type TargetTableFormValues = z.infer<typeof targetTableFormSchema>;
export type MappingFieldsTableValues = z.infer<typeof mappingFieldsTableSchema>;
export type TsaFieldsTableValues = z.infer<typeof tsaFieldsTableSchema>;
